import React, { useCallback, useEffect, useMemo, useState } from "react";
import { store } from "../../redux/store/store";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import { urlDelPortal } from "../../helpers/zoco";
import { plantillasChatHref } from "../components/Whatsapp";
import { chatListTemplatesApi, chatListWorkspacesApi } from "../../api/organizerChatApi";
import { CAMPOS_WHATSAPP, PUSH_TEXTO_MAX, PUSH_TITULO_MAX, PUSH_VARIABLES, bloqueoDeWorkspace, parametrosPorDefecto, pushTextosPorDefecto } from "./recordatorios";
import { PLANTILLA_SIN_VERIFICAR, bloqueoDePlantilla, estadoDePlantilla, mensajeDeChat, sePuedeReintentar, textoDeVistaPrevia } from "./plantillasWhatsapp";
import PlantillaNuevaModal from "./PlantillaNuevaModal";
import VistaPreviaWhatsapp from "./VistaPreviaWhatsapp";
import "./recordatorios.css";

/* WhatsApp channel box of the reminder settings (owner only). Reads the Zoco Chat workspaces and templates through the
   organizer proxy, maps each template variable to a reminder field and previews the message with sample values.
   `whatsapp` is the form value `{ enabled, workspaceId, templateName, language, parameters }`. */

const IconoWhatsapp = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.935 11.935 0 0 0 12 0C5.373 0 0 5.373 0 12c0 2.111.548 4.182 1.588 6.01L0 24l6.153-1.566A11.93 11.93 0 0 0 12 24c6.627 0 12-5.373 12-12 0-3.19-1.244-6.188-3.48-8.52ZM12 22.001a9.96 9.96 0 0 1-5.08-1.393l-.364-.214-3.633.925.97-3.54-.237-.372A9.957 9.957 0 0 1 2 12C2 6.486 6.486 2 12 2c2.668 0 5.182 1.04 7.07 2.929C20.96 6.818 22 9.332 22 12c0 5.514-4.486 10.001-10 10.001Zm5.204-7.58c-.285-.143-1.684-.83-1.944-.924-.26-.095-.45-.143-.64.143-.19.285-.734.924-.9 1.114-.167.19-.334.214-.62.071-.285-.143-1.204-.444-2.293-1.417-.848-.756-1.42-1.69-1.587-1.975-.167-.285-.018-.439.125-.582.128-.128.285-.334.428-.5.143-.167.19-.285.285-.476.095-.19.047-.357-.024-.5-.071-.143-.64-1.54-.876-2.11-.23-.553-.465-.478-.64-.486-.167-.008-.357-.01-.547-.01-.19 0-.5.071-.762.357-.262.285-1.002.978-1.002 2.386s1.026 2.768 1.169 2.957c.143.19 2.02 3.086 4.89 4.325.683.294 1.215.47 1.63.603.685.218 1.31.188 1.803.114.55-.082 1.684-.69 1.922-1.357.238-.666.238-1.237.167-1.357-.071-.119-.262-.19-.547-.333Z" />
  </svg>
);

const IconoMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const IconoFlecha = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const IconoMas = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const IconoInfo = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

// Texts that name the message: reminders (default), the review request and the waitlist notice.
const TEXTOS = {
  recordatorios: { salen: "los recordatorios salen", pueden: "Los recordatorios pueden", ese: "ese recordatorio sale" },
  resena: { salen: "el pedido sale", pueden: "El pedido puede", ese: "ese pedido sale" },
  lista: { salen: "el aviso sale", pueden: "El aviso puede", ese: "ese aviso sale" },
  campana: { salen: "la campaña sale", pueden: "La campaña puede", ese: "esa campaña sale" },
};

/* `campos` is the list of data a template variable can carry (reminders by default); `idBase` keeps the control ids
   unique when two channels share a form; `etiqueta` names the switch and `pedido` the message in the texts. */
const CanalWhatsapp = ({
  whatsapp,
  onChange,
  local = "",
  error = "",
  puedeCrear = true,
  onAntesDeIrAChat,
  volver,
  campos = CAMPOS_WHATSAPP,
  idBase = "wa",
  etiqueta = "Recordatorios por WhatsApp",
  pedido = "recordatorios",
  categoria = "UTILITY",
}) => {
  const [workspaces, setWorkspaces] = useState(null);
  const [errorWs, setErrorWs] = useState(null);
  const [plantillas, setPlantillas] = useState(null);
  const [errorPl, setErrorPl] = useState(null);
  const [creando, setCreando] = useState(false);

  const cargarWorkspaces = useCallback(() => {
    setErrorWs(null);
    setWorkspaces(null);
    return chatListWorkspacesApi()
      .then((res) => setWorkspaces(Array.isArray(res?.data?.info) ? res.data.info : []))
      .catch((e) => setErrorWs({ mensaje: mensajeDeChat(e, "No pudimos cargar tus workspaces de Zoco Chat."), reintentar: sePuedeReintentar(e) }));
  }, []);

  // Nothing is requested until the venue switches the channel on.
  useEffect(() => {
    if (whatsapp.enabled) cargarWorkspaces();
  }, [whatsapp.enabled, cargarWorkspaces]);

  const conectados = useMemo(() => (workspaces || []).filter((w) => w.whatsappConectado), [workspaces]);
  const workspace = conectados.find((w) => w.workspaceId === whatsapp.workspaceId) || null;
  const plantilla = (plantillas || []).find((t) => t.name === whatsapp.templateName) || null;
  // Workspace problems first, then the template: only an APPROVED one can be saved.
  const bloqueoPlantilla = bloqueoDePlantilla({ templateName: whatsapp.templateName, plantilla, cargada: plantillas !== null });
  const bloqueo = (workspace ? bloqueoDeWorkspace(workspace) : "") || bloqueoPlantilla;
  const sinMetodo = workspace?.metodoDePago === "falta";
  const bloqueado = workspace?.puedeEnviar === "BLOCKED";
  const limitado = workspace?.puedeEnviar === "LIMITED";
  const sinVerificar = workspace && !sinMetodo && !bloqueado && (workspace.metodoDePago === "desconocido" || workspace.puedeEnviar === "UNKNOWN");
  const negocioId = store.getState?.()?.auth?.negocio?.id;
  const hrefGestion = workspace ? plantillasChatHref(workspace.workspaceId, volver) : null;

  const cargarPlantillas = useCallback((workspaceId) => {
    setErrorPl(null);
    setPlantillas(null);
    return chatListTemplatesApi(workspaceId)
      .then((res) => setPlantillas(Array.isArray(res?.data?.info) ? res.data.info : []))
      .catch((e) => setErrorPl({ mensaje: mensajeDeChat(e, "No pudimos cargar las plantillas de WhatsApp."), reintentar: sePuedeReintentar(e) }));
  }, []);

  useEffect(() => {
    if (whatsapp.enabled && whatsapp.workspaceId && workspace) cargarPlantillas(whatsapp.workspaceId);
    else setPlantillas(null);
    // Reload only when the channel is switched on or the workspace changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsapp.enabled, whatsapp.workspaceId, Boolean(workspace), cargarPlantillas]);

  // A single connected workspace is the only possible choice: pick it as soon as the channel is switched on.
  useEffect(() => {
    if (whatsapp.enabled && !whatsapp.workspaceId && conectados.length === 1) {
      onChange({ ...whatsapp, workspaceId: conectados[0].workspaceId });
    }
  }, [whatsapp, conectados, onChange]);

  // The form value carries the block so the policy forms refuse to save (faltaWhatsapp reads it).
  useEffect(() => {
    if (whatsapp.enabled && (whatsapp.bloqueo || "") !== bloqueo) onChange({ ...whatsapp, bloqueo });
  }, [whatsapp, bloqueo, onChange]);

  const cantidad = plantilla ? Number(plantilla.variableCount) || 0 : whatsapp.parameters.length;
  const estado = plantilla ? String(plantilla.status || "").toUpperCase() : "";

  const elegirWorkspace = (workspaceId) => {
    if (workspaceId === whatsapp.workspaceId) return;
    onChange({ ...whatsapp, workspaceId, templateName: "", parameters: [] });
  };

  const elegirPlantilla = (templateName) => {
    const elegida = (plantillas || []).find((t) => t.name === templateName);
    onChange({ ...whatsapp, templateName, parameters: parametrosPorDefecto(Number(elegida?.variableCount) || 0, campos) });
  };

  const elegirDato = (indice, campo) => {
    const parameters = Array.from({ length: cantidad }, (_, i) => whatsapp.parameters[i] || "");
    parameters[indice] = campo;
    onChange({ ...whatsapp, parameters });
  };

  const alCrear = ({ name, parametros }) => {
    cargarPlantillas(whatsapp.workspaceId);
    onChange({ ...whatsapp, templateName: name, parameters: parametros });
  };

  const opcionesPlantilla = (plantillas || []).map((t) => ({
    value: t.name,
    label: `${t.name} · ${estadoDePlantilla(t.status).label}`,
    disabled: String(t.status || "").toUpperCase() === "REJECTED",
  }));

  const cuerpo = (
    <>
      {error && <div className="ef-alert ef-alert--danger" role="alert">{error}</div>}
      {workspaces === null && !errorWs && <p className="rwa-estado" role="status">Cargando tus workspaces de Zoco Chat…</p>}
      {errorWs && (
        <div className="ef-alert ef-alert--danger rwa-estado--error" role="alert">
          <span>{errorWs.mensaje}</span>
          {errorWs.reintentar && <button type="button" className="zr-btn zr-btn--soft" onClick={cargarWorkspaces}>Reintentar</button>}
        </div>
      )}
      {workspaces !== null && conectados.length === 0 && (
        <>
          <div className="fc-empty">
            <p>Ningún workspace de Zoco Chat tiene WhatsApp conectado.</p>
            <p>Conectá un número en Zoco Chat y volvé acá para elegir o crear una plantilla.</p>
            <a className="zr-btn zr-btn--soft" href={urlDelPortal(`/api/auth/autorizar?producto=Chats${negocioId ? `&negocio=${negocioId}` : ""}`)}>
              Conectar WhatsApp en Zoco Chat
            </a>
          </div>
          <div className="zr-info" role="status">
            <IconoInfo />
            <span>Mientras tanto, {(TEXTOS[pedido] || TEXTOS.resena).salen} solo por email.</span>
          </div>
        </>
      )}
      {conectados.length > 0 && (
        <>
          <div className="rwa-datos">
            <div className="zr-field">
              <span id={`${idBase}-ws-label`} className="zr-field__label">Workspace de Zoco Chat</span>
              <OrganizerPillSelect
                id={`${idBase}-ws`}
                labelledBy={`${idBase}-ws-label`}
                value={whatsapp.workspaceId}
                onChange={elegirWorkspace}
                options={conectados.map((w) => ({ value: w.workspaceId, label: bloqueoDeWorkspace(w) ? `${w.nombre} · con problemas` : w.nombre }))}
              />
              {workspace && (
                <p className="rwa-conectado"><span className="dot" />WhatsApp conectado</p>
              )}
            </div>
            <div className="zr-field">
              <span id={`${idBase}-tpl-label`} className="zr-field__label">Plantilla</span>
              {workspace && plantillas === null && !errorPl && <p className="rwa-estado" role="status">Cargando plantillas…</p>}
              {errorPl && (
                <div className="ef-alert ef-alert--danger rwa-estado--error" role="alert">
                  <span>{errorPl.mensaje}</span>
                  {errorPl.reintentar && (
                    <button type="button" className="zr-btn zr-btn--soft" onClick={() => cargarPlantillas(whatsapp.workspaceId)}>
                      Reintentar
                    </button>
                  )}
                </div>
              )}
              {workspace && plantillas !== null && (
                <>
                  <OrganizerPillSelect
                    id={`${idBase}-tpl`}
                    labelledBy={`${idBase}-tpl-label`}
                    value={whatsapp.templateName}
                    onChange={elegirPlantilla}
                    options={opcionesPlantilla}
                    placeholder={opcionesPlantilla.length ? "Elegir plantilla" : "Todavía no hay plantillas"}
                  />
                  {puedeCrear && (

                    <button type="button" className="zr-btn zr-btn--soft" onClick={() => setCreando(true)}>

                      <IconoMas />Crear plantilla nueva

                    </button>

                  )}
                  {puedeCrear && hrefGestion && (
                    <a className="zr-btn zr-btn--soft" href={hrefGestion} onClick={onAntesDeIrAChat}>
                      Gestionar plantillas en Zoco Chat
                    </a>
                  )}
                </>
              )}
              {!workspace && <p className="rwa-estado">Elegí primero el workspace.</p>}
            </div>
          </div>

          {sinMetodo && (
            <div className="ef-alert ef-alert--danger rwa-estado--error" role="alert">
              <span>Esta cuenta de WhatsApp no tiene un método de pago. Meta no permite enviar plantillas sin uno. Cargalo en el Business Manager de Meta y volvé a intentar.</span>
              <button type="button" className="zr-btn zr-btn--soft" onClick={cargarWorkspaces}>Volver a verificar</button>
            </div>
          )}
          {bloqueado && (
            <div className="ef-alert ef-alert--danger" role="alert">
              <p>Meta bloqueó los envíos de esta cuenta de WhatsApp.</p>
              {(workspace.problemas || []).length > 0 && (
                <ul className="rwa-problemas">
                  {workspace.problemas.map((p, i) => (
                    <li key={`${p.codigo}-${i}`}>{p.descripcion}{p.solucion ? ` ${p.solucion}` : ""}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {limitado && (
            <div className="ef-alert ef-alert--warning" role="status">
              Meta limitó los envíos de esta cuenta de WhatsApp. {(TEXTOS[pedido] || TEXTOS.resena).pueden} salir con demora o no salir.
            </div>
          )}
          {sinVerificar && <p className="rwa-estado" role="status">No pudimos verificar el método de pago.</p>}

          {bloqueoPlantilla === PLANTILLA_SIN_VERIFICAR && !errorPl && !errorWs && workspaces !== null && !workspace && (
            <div className="ef-alert ef-alert--danger rwa-estado--error" role="alert">
              <span>{PLANTILLA_SIN_VERIFICAR}.</span>
              <button type="button" className="zr-btn zr-btn--soft" onClick={cargarWorkspaces}>Volver a verificar</button>
            </div>
          )}
          {estado === "PENDING" && (
            <div className="ef-alert ef-alert--warning" role="status">
              La plantilla todavía está en revisión de Meta. Vas a poder guardar cuando esté aprobada.
            </div>
          )}
          {estado === "REJECTED" && (
            <div className="ef-alert ef-alert--danger" role="alert">
              Meta rechazó esta plantilla{plantilla.rejectionReason ? `: ${plantilla.rejectionReason}` : "."} Elegí otra o creá una nueva.
            </div>
          )}

          {plantilla && estado !== "REJECTED" && (
            <div className="rwa-split">
              <div className="zr-field">
                <span id={`${idBase}-vars-label`} className="zr-field__label">Variables de la plantilla</span>
                {cantidad > 0 ? (
                  <>
                    <p className="ef-footnote">
                      La plantilla tiene {cantidad} {cantidad === 1 ? "variable" : "variables"}. Elegí qué dato va en {cantidad === 1 ? "ella" : "cada una"}.
                    </p>
                    <div className="rwa-vars" role="group" aria-labelledby={`${idBase}-vars-label`}>
                      {Array.from({ length: cantidad }, (_, i) => (
                        <div className="rwa-var" key={i}>
                          <span id={`${idBase}-var-${i + 1}-n`} className="rwa-var__n" aria-label={`Variable ${i + 1}`}>{`{{${i + 1}}}`}</span>
                          <span className="rwa-var__flecha" aria-hidden="true"><IconoFlecha /></span>
                          <OrganizerPillSelect
                            id={`${idBase}-var-${i + 1}`}
                            labelledBy={`${idBase}-var-${i + 1}-n`}
                            value={whatsapp.parameters[i] || ""}
                            onChange={(val) => elegirDato(i, val)}
                            options={campos}
                            placeholder="Elegir dato"
                          />
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="ef-footnote">La plantilla no tiene variables: sale igual para todos los clientes.</p>
                )}
              </div>
              <VistaPreviaWhatsapp
                local={local}
                texto={textoDeVistaPrevia(plantilla.bodyText, whatsapp.parameters, local)}
              />
            </div>
          )}

          <div className="zr-info" role="note">
            <IconoInfo />
            <span>El cliente tiene que haber dejado un celular válido. Si no tiene, {(TEXTOS[pedido] || TEXTOS.resena).ese} solo por email.</span>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="zr-box">
      <div className="zr-row-toggle">
        <div className="zr-row-toggle__txt">
          <div className="t rwa-t">
            <IconoWhatsapp />
            WhatsApp
            {estado === "PENDING" && whatsapp.enabled && (
              <span className="zr-badge zr-badge--pending"><span className="dot" />Pendiente de aprobación</span>
            )}
          </div>
          <div className="h">Se envía con una plantilla de Zoco Chat, desde el número de tu salón.</div>
        </div>
        <input
          type="checkbox"
          className="zr-switch"
          aria-label={etiqueta}
          checked={Boolean(whatsapp.enabled)}
          onChange={() => onChange({ ...whatsapp, enabled: !whatsapp.enabled })}
        />
      </div>
      {whatsapp.enabled && cuerpo}
      {puedeCrear && creando && workspace && (
        <PlantillaNuevaModal
          campos={campos}
          categoria={categoria}
          workspaceId={workspace.workspaceId}
          workspaceNombre={workspace.nombre}
          local={local}
          onClose={() => setCreando(false)}
          onCreada={alCrear}
        />
      )}
    </div>
  );
};

const IconoCampana = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

/**
 * In-app notification channel: a switch and, when on, the title and text (with variables) of the notification.
 * `push` is `{ enabled, title?, body? }` (pushDeVenue); a text the venue never wrote shows the default for `tipo`
 * ("reminder", "review" or "waitlist"; `sustantivo` is "turno" or "reserva" for the reminder).
 */
export const CanalPush = ({ push, onChange, etiqueta = "Notificación en la app", tipo = "reminder", sustantivo = "reserva" }) => {
  const porDefecto = pushTextosPorDefecto(tipo, sustantivo);
  const title = typeof push?.title === "string" ? push.title : porDefecto.title;
  const body = typeof push?.body === "string" ? push.body : porDefecto.body;
  const enabled = Boolean(push?.enabled);
  const emitir = (cambio) => onChange({ enabled, title, body, ...cambio });
  const idBase = `push-${tipo}`;
  return (
    <div className="zr-box">
      <div className="zr-row-toggle">
        <div className="zr-row-toggle__txt">
          <div className="t rwa-t"><IconoCampana />Notificación en la app</div>
          <div className="h">Llega a los clientes con cuenta que tienen la app de Zoco Tickets.</div>
        </div>
        <input
          type="checkbox"
          className="zr-switch"
          aria-label={etiqueta}
          checked={enabled}
          onChange={() => emitir({ enabled: !enabled })}
        />
      </div>
      {enabled && (
        <div className="zr-field">
          <label htmlFor={`${idBase}-titulo`} className="zr-field__label">Título</label>
          <input
            id={`${idBase}-titulo`}
            type="text"
            className="form-control"
            value={title}
            placeholder={porDefecto.title}
            maxLength={PUSH_TITULO_MAX}
            onChange={(e) => emitir({ title: e.target.value })}
          />
          <small style={{ color: "#6b7280", fontSize: 12 }}>{title.length}/{PUSH_TITULO_MAX}</small>
          <label htmlFor={`${idBase}-texto`} className="zr-field__label">Texto</label>
          <textarea
            id={`${idBase}-texto`}
            className="form-control"
            rows={3}
            value={body}
            placeholder={porDefecto.body}
            maxLength={PUSH_TEXTO_MAX}
            onChange={(e) => emitir({ body: e.target.value })}
          />
          <small style={{ color: "#6b7280", fontSize: 12 }}>{body.length}/{PUSH_TEXTO_MAX}</small>
          <small style={{ color: "#6b7280", fontSize: 12, display: "block" }}>
            Podés usar {PUSH_VARIABLES.join(", ")}: se reemplazan por el nombre del cliente, el local, el día y la hora. Si lo dejás vacío, se usa el texto de siempre.
          </small>
        </div>
      )}
    </div>
  );
};

export const CanalEmail = ({ texto = "Le llega al email que el cliente dejó al reservar. Sigue funcionando como hasta ahora." }) => (
  <div className="zr-box">
    <div className="zr-row-toggle">
      <div className="zr-row-toggle__txt">
        <div className="t rwa-t"><IconoMail />Email</div>
        <div className="h">{texto}</div>
      </div>
      <span className="zr-badge"><span className="dot" />Siempre activo</span>
    </div>
  </div>
);

export default CanalWhatsapp;
